"use client";

import dynamic from "next/dynamic";
import { redirect, useRouter } from "next/navigation";
import { useContext, useEffect, useState } from "react";
import TagManager from "react-gtm-module";
import { useDispatch, useSelector } from "react-redux";

import LoadingOverlay from "@/components/molecules/loading-overlay/loading-overlay";
declare global {
  interface Window {
    zE?: (action: string, subaction: string, value: string[]) => void;
  }
}
import APIErrorModalRenewal from "@/components/molecules/renewals/renewal-error/renewal-error";
import {
  NotFound,
  QuoteErrorCodesEnum,
  RenewalPage,
  TurnstileFEErrorCodesEnum,
  USER_DISPLAYED_TURNSTILE_ERRORS,
  USER_HIDDEN_TURNSTILE_ERRORS,
} from "@/config/app-constant";
import { APPLICATION, MY_ACCOUNT_URL, PRODUCT_DOMAIN } from "@/constants";
import { ContactNumberContext } from "@/hooks/contact-number-context/contact-number-context";
import useGtmTurnstilePopup from "@/hooks/gtm/use-gtm-turnstile-popup";
import { useOnetrustInertRemoval } from "@/hooks/use-onetrust-inert-removal/use-onetrust-inert-removal";
import { useApplication } from "@/providers/application-context-provider";
import { RootState } from "@/store/root-reducer";
import { clearApiError } from "@/store/slices/errors/api-error.slice";
import { openHelpOverlay } from "@/store/slices/help-overlay.slice";
import { clearMedicalData } from "@/store/slices/medical.slice";
import { clearQuestion, clearQuote } from "@/store/slices/quote/quote.slice";
import { getCookieValue } from "@/utils/common.utils";
import { showMessenger } from "@/utils/cxone.utils";
import { scrollToTop } from "@/utils/scrollUtils";

import DatadogInit from "./datadog-init";

declare global {
  interface Window {
    zE?: (action: string, subaction: string, value: string[]) => void;
  }
}

const APIErrorModal = dynamic(
  () => import("@/components/molecules/api-error-modal/api-error-modal")
);

if (typeof window !== "undefined") {
  if (APPLICATION === "renewal") {
    const params = new URLSearchParams(window.location.search);
    const quoteId = params?.get("quoteId");
    const affiliate = params?.get("affiliate");
    if (!quoteId && !affiliate) {
      const confirmationLoaded = sessionStorage.getItem("confirmationLoaded");
      if (confirmationLoaded) {
        window.location.href = MY_ACCOUNT_URL!;
      }
    }
  }
}

/**
 * Application-wide client chrome (loading overlay, API error modals, GTM,
 * Datadog). This was `app/template.tsx` until the Next.js 16 upgrade; it is now
 * rendered directly by `app/layout.tsx`.
 *
 * It must NOT be a root `template.tsx` again. Next.js 16 prerenders the
 * synthetic `/_global-error` route with the root *layout* filtered out but the
 * root *template* still in the tree (see `next-app-loader`, which only filters
 * `type !== 'layout'` for that route), so a root template reading Redux crashes
 * `next build` with "Cannot destructure property 'store' of ... as it is null".
 *
 * Mounting it from the root layout preserves the original lifecycle. A
 * template's identity is keyed on its *child* segment, and the root segment's
 * only child is the `(pages)` route group, so the old root template already
 * mounted exactly once per document load and never remounted on client
 * navigation. Layout placement reproduces that. Moving it any deeper — e.g. into
 * `app/(pages)/template.tsx`, whose child segment changes per page — would
 * remount it on every navigation and re-run `TagManager.initialize`.
 */
export default function AppShell({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  const dispatch = useDispatch();
  const router = useRouter();
  const {
    errorStatus,
    modalTitle,
    modalDescription,
    ctaPrimaryBtnLabel,
    ctaSecondaryBtnLabel,
    multiButton,
    httpCode,
    errorCode,
    errors,
    validationEnable,
    redirectPath,
    onPrimaryCtaClick,
    isCloseIconEnabled,
    onCloseClick,
    contactNumber,
  } = useSelector((state: RootState) => state.apiError);

  const { turnstileErrorCode } = useSelector((state: RootState) => state.quote);

  const [isOpenModal, setIsOpenModal] = useState(false);

  const { active } = useContext(ContactNumberContext);

  const phoneNumber = /\d+/.exec(active.customerService.split(" ").join(""));

  const phoneLink = `tel:${phoneNumber}`;

  const { turnstile } = useApplication();

  const isTurnstileError =
    typeof turnstileErrorCode === "string" &&
    /^(300|600)\d{3}$/.test(turnstileErrorCode) &&
    !USER_HIDDEN_TURNSTILE_ERRORS.includes(
      turnstileErrorCode as TurnstileFEErrorCodesEnum
    );
  const isTurnstileBEError =
    httpCode === 403 &&
    USER_DISPLAYED_TURNSTILE_ERRORS.includes(errors?.[0]?.code as number);

  useEffect(() => {
    if (errorStatus && httpCode === 401) {
      if (process.env.NEXT_PUBLIC_APPLICATION === RenewalPage) {
        setIsOpenModal(true);
        return;
      }
      dispatch(clearApiError());
      dispatch(clearQuestion());
      router.replace("/");
    } else if (errorStatus) {
      setIsOpenModal(true);
    }
  }, [dispatch, isOpenModal, errorStatus, httpCode, router]);

  // Scroll to top when API error modal opens
  useEffect(() => {
    if (isOpenModal) {
      scrollToTop();
    }
  }, [isOpenModal]);

  useEffect(() => {
    setTimeout(() => {
      if (document.cookie) {
        const OptanonAlertBoxClosed = getCookieValue(
          "OptanonAlertBoxClosed",
          document.cookie
        );
        if (OptanonAlertBoxClosed) {
          if (
            typeof window !== "undefined" &&
            window.OneTrust &&
            typeof window.OneTrust.Close === "function"
          ) {
            window.OneTrust.Close();
          }
        }
      }
    }, 2000);
    const handleConsentOneTrust = () => {
      // e contains information about the event, you can use it if needed
      const element = document.getElementById("ot-sdk-btn-floating");
      if (element) {
        element.style.display = "none";
      }
    };

    // Add event listener for "consent.onetrust"
    window.addEventListener("consent.onetrust", handleConsentOneTrust);

    // Clean up function to remove the event listener when component unmounts
    return () => {
      window.removeEventListener("consent.onetrust", handleConsentOneTrust);
    };
  }, []);

  // Remove React Aria's `inert` from the OneTrust container while the error
  // modal is open, so cookie consent buttons stay clickable (SST-9055).
  useOnetrustInertRemoval(isOpenModal);

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_ENV === "production") {
      TagManager.initialize({
        gtmId: process.env.NEXT_PUBLIC_GTM_ID ?? "",
        auth: process.env.NEXT_PUBLIC_GTM_AUTH ?? "",
        preview: process.env.NEXT_PUBLIC_GTM_PREVIEW ?? "",
      });
    }

    if (window) {
      if (window.location.pathname === "/404/") {
        redirect(NotFound);
      }
    }
  }, []);

  const handleModalClose = (value: boolean) => {
    // Check if MEDICAL_DECLINED and handle redirect on close
    const isMedicalDeclined =
      errors?.[0]?.code === QuoteErrorCodesEnum.MEDICAL_DECLINED;

    if (isMedicalDeclined && redirectPath) {
      router.push(redirectPath);
    }

    if (onCloseClick) {
      onCloseClick();
    }
    dispatch(clearApiError());
    setIsOpenModal(value);
  };

  const handlePrimaryButtonClick = (value: boolean) => {
    // "Get a new quote" on a medically declined quote goes to the cover-type
    // page ("/"). Checked before `redirectPath`, which for this error holds the
    // close-icon destination (the decline page) that the CTA must not use.
    // Renewal has its own decline handling and no "/" journey.
    if (
      errors?.[0]?.code === QuoteErrorCodesEnum.MEDICAL_DECLINED &&
      APPLICATION !== RenewalPage
    ) {
      dispatch(clearQuote());
      dispatch(clearMedicalData());
      router.push("/");
      dispatch(clearApiError());
      setIsOpenModal(false);
      return;
    }

    // If a redirectPath is provided, navigate to it (new serializable approach)
    if (redirectPath) {
      router.push(redirectPath);
      dispatch(clearApiError());
      setIsOpenModal(false);
      return;
    }

    // Legacy: If onPrimaryCtaClick function exists, use it
    if (onPrimaryCtaClick) {
      onPrimaryCtaClick({ errorCode, httpCode, errors, handleModalClose });
      setIsOpenModal(false);
      return;
    }

    // Existing fallback logic for errors without redirectPath
    const isError = errorCode === "456";
    const isQuoteExpiredError =
      errors?.[0]?.code === QuoteErrorCodesEnum.EXPIRED;
    const isMedicalDeclined =
      errors?.[0]?.code === QuoteErrorCodesEnum.MEDICAL_DECLINED;
    const isQuotePurchased =
      errors?.[0]?.code === QuoteErrorCodesEnum.ALREADY_PURCHASED;

    if (
      isError ||
      isQuoteExpiredError ||
      isMedicalDeclined ||
      isQuotePurchased ||
      isTurnstileError ||
      isTurnstileBEError
    ) {
      window.location.href = "/";
    }
    dispatch(clearApiError());
    setIsOpenModal(value);
  };

  const handleCallClickExpat = () => {
    window.location.href = phoneLink;
  };

  const triggerHelpOverlay = () => {
    const helpOverlay = document.getElementById("helpoverlay-automate-test-id");
    if (helpOverlay) {
      dispatch(openHelpOverlay());
    }
  };

  const handleContactUs = () => {
    if (multiButton) {
      dispatch(clearApiError());
      setIsOpenModal(false);

      if (PRODUCT_DOMAIN === "EXP") {
        handleCallClickExpat();
      } else if (PRODUCT_DOMAIN === "IRE" || PRODUCT_DOMAIN === "ESP") {
        triggerHelpOverlay();
      } else {
        showMessenger();
      }
    }
  };

  // Need to fix this according to the requirements
  useGtmTurnstilePopup({
    isPopupOpen: isTurnstileError || isTurnstileBEError,
    frontendErrorCode: turnstileErrorCode ?? "",
    backendErrorCode: errors?.[0]?.code ?? undefined,
  });

  return (
    <>
      {process.env.NEXT_PUBLIC_DD_ENABLED === "true" && <DatadogInit />}
      <LoadingOverlay />
      <div>
        {isOpenModal && APPLICATION !== RenewalPage && (
          <APIErrorModal
            modalId={
              isTurnstileError || isTurnstileBEError
                ? "turnstile-error-modal"
                : "api-error-modal"
            }
            isCloseIconEnabled={!isTurnstileBEError && isCloseIconEnabled}
            title={
              isTurnstileBEError ? turnstile?.titleText ?? "Error" : modalTitle
            }
            description={
              isTurnstileBEError
                ? turnstile?.descriptionText ?? "Bot"
                : modalDescription
            }
            ctaPrimaryBtnLabel={
              isTurnstileBEError
                ? turnstile?.ctaButtonOneText
                : ctaPrimaryBtnLabel
            }
            ctaSecondaryBtnLabel={
              isTurnstileBEError ? undefined : ctaSecondaryBtnLabel
            }
            isSecondaryCtaEnabled={!isTurnstileBEError && multiButton}
            errors={errors}
            isOpenModal={isOpenModal && APPLICATION !== RenewalPage}
            validationEnable={validationEnable}
            onOpenChange={(value) => handleModalClose(value)}
            onPrimaryCtaClick={(value) =>
              onPrimaryCtaClick
                ? onPrimaryCtaClick({
                    errorCode,
                    httpCode,
                    errors,
                    handleModalClose,
                  })
                : handlePrimaryButtonClick(value)
            }
            onSecondaryCtaClick={() => handleContactUs()}
            contactNumber={contactNumber}
          />
        )}
        {isOpenModal && APPLICATION === RenewalPage && (
          <div className="relative z-[10] ">
            <APIErrorModalRenewal
              isCloseIconEnabled={false}
              title={modalTitle}
              ctaPrimaryBtnLabel={ctaPrimaryBtnLabel}
              isOpenModal={isOpenModal && APPLICATION === RenewalPage}
              description={modalDescription}
              onPrimaryCtaClick={(value) =>
                onPrimaryCtaClick
                  ? onPrimaryCtaClick({
                      errorCode,
                      httpCode,
                      errors,
                      handleModalClose,
                    })
                  : handlePrimaryButtonClick(value)
              }
            />
          </div>
        )}
        <div
          className={`${
            isOpenModal && APPLICATION === RenewalPage && "z-[9] relative"
          }`}
        >
          {children}
        </div>
      </div>
    </>
  );
}
